import { api } from "@/shared/api";

const BASE = "/tenant/geo";

/** 监测轮次摘要(列表 + 发起回执)。 */
export interface GeoRunSummary {
  id: number;
  brand: string;
  industry: string | null;
  overallScore: number | null;
  grade: string | null;
  status: string; // RUNNING | SUCCESS | FAILED
  /** S1 基线机制:该轮是否基线对照面(首轮 SUCCESS 自动;可手动改指)。 */
  baseline?: boolean;
  createdAt: string;
}

/** 监测轮次详情(轮询)。 */
export interface GeoRunDetail extends GeoRunSummary {
  errorMessage: string | null;
  reportJson: string | null;
}

/** 单条探针(AI 原话回放墙数据;A3 起带解析增强字段,旧报告缺键走可选)。 */
export interface GeoProbe {
  platform: string;
  stage: string; // awareness | compare | decide | doubt
  question: string;
  mentioned: boolean;
  rank?: number | null;
  sentiment?: string; // positive | neutral | negative
  competitors?: string[];
  answer_excerpt?: string;
  intent?: string;
  is_recommended?: boolean;
  reasons?: string[];
  weaknesses?: string[];
  answer_full?: string;
  /** E1 双模 + S1 换源:memory=参数记忆 | search=联网检索 | derived=生态推演 */
  mode?: string;
  citations?: { title?: string; url?: string; domain?: string; media?: string }[];
  /** S1-T2 证据十字段:徽标判定唯一真源 = provenance(禁止从文案猜)。 */
  provenance?: "real" | "derived";
  citation_urls?: string[];
  status_code?: number | null;
  latency_ms?: number | null;
  model?: string;
  collected_at?: string;
}

/** E2 检索面洞察(ai soc.build_search_insight;旧报告/未配联网引擎缺位)。 */
export interface GeoSearchInsight {
  available: boolean;
  engines?: { name: string; label: string; citations_supported: boolean }[];
  probe_count?: number;
  search_score?: number;
  soc?: {
    available: boolean;
    total: number;
    own: number;
    competitor: number;
    neutral: number;
    own_pct: number;
    competitor_pct: number;
    neutral_pct: number;
    competitor_breakdown?: [string, number][];
  };
  engine_attribution?: {
    engine: string;
    label: string;
    citations_supported: boolean;
    total: number;
    own: number;
    own_pct: number;
    top_domains: [string, number, string][];
  }[];
  source_priorities?: { core: string[]; quality: string[]; supplement: string[] };
}

export interface GeoCitationSource {
  domain: string;
  media: string;
  count: number;
  latest_date?: string | null;
  source_type?: string; // encyclopedia | news | qa_social | video | government | other
}

/** 信源阵地体检(A2;旧报告无此键)。 */
export interface GeoPresence {
  available?: boolean;
  items?: {
    key: string;
    label: string;
    present: boolean;
    evidence_url?: string;
    evidence_title?: string;
  }[];
  competitor_gaps?: { key: string; label: string; competitor: string; evidence_url?: string }[];
}

/** 内容策略卡(A3「怎么写·怎么投」;旧报告只有 titles)。 */
export interface GeoTopic {
  title: string;
  points: string[];
  channel: string;
  timing: string;
  priority: "P0" | "P1" | "P2";
  evidence?: string;
  target_question?: string; // 这篇内容直接回答的「竞对占位」原提问
  outline?: string[]; // 文章结构建议(3-5 条)
  keywords?: string[]; // 要植入的搜索词/品牌词/问答长尾词
}

/* ── S1 换源批新段类型(2026-07-27;全部可选,旧报告缺键 → 对应板块回落/隐藏)── */

/** 评分构成单项(score_breakdown.components 的一项;项数随 score_version,v2.2-evidence 为 7 项)。 */
export interface GeoScoreComponent {
  key: string;
  label: string;
  weight: number;
  score: number;
  weighted: number;
}

export interface GeoScoreBreakdown {
  score_version?: string;
  overall?: number;
  grade?: string;
  components?: GeoScoreComponent[];
  real_samples?: number;
  expected_real_samples?: number;
  negative_rate?: number;
  denominator_note?: string;
}

export interface GeoAudienceSegment {
  name: string;
  valid_answers: number;
  mention_rate: number;
  positive_rate: number;
  negative_rate: number;
  status: string;
  focus: string;
  top_platform: string;
}

export interface GeoAudienceStats {
  available?: boolean;
  basis?: string;
  segments?: GeoAudienceSegment[];
}

export interface GeoTrustAsset {
  key: string;
  label: string;
  verified: boolean;
  value: string;
  evidence?: { title?: string; url?: string; domain?: string };
}

export interface GeoTrustAssets {
  available?: boolean;
  assets?: GeoTrustAsset[];
  verified_count?: number;
  basis?: string;
}

export interface GeoSearchOverview {
  available?: boolean;
  engines?: string[];
  question_count?: number;
  valid_answers?: number;
  mention_hits?: number;
  recommend_hits?: number;
  negative_hits?: number;
  citation_url_count?: number;
  valid_domain_count?: number;
  /** 1005:与提及率同一引用池的本品牌位次(ai report_sections.build_brand_rank);0 提及时 position=null、label=「未被提及」。 */
  brand_rank?: { brand_mentions: number; position: number | null; total: number; label: string };
  stage_distribution?: Record<string, number>;
  intent_distribution?: Record<string, number>;
  source_distribution?: { domain: string; media: string; count: number }[];
  samples?: {
    question: string;
    platform: string;
    platform_label: string;
    answer_excerpt: string;
    citation_urls: string[];
  }[];
}

export interface GeoQuestionRow {
  question: string;
  stage: string;
  intent: string;
  mentioned: boolean;
  sentiment: string;
  is_recommended: boolean;
  answer_excerpt: string;
  citation_domains: string[];
  provenance: "real" | "derived";
}

export interface GeoPlatformDetail {
  platform: string;
  label: string;
  /** 徽标唯一真源:real=联网实测 / derived=生态推演。 */
  provenance: "real" | "derived";
  samples: number;
  mention_rate: number;
  positive_rate: number;
  negative_rate: number;
  sentiment_dist: { positive: number; neutral: number; negative: number };
  model: string;
  questions: { positive: GeoQuestionRow[]; negative: GeoQuestionRow[] };
}

export interface GeoPlatformDetails {
  available?: boolean;
  real_count?: number;
  derived_count?: number;
  platforms?: GeoPlatformDetail[];
}

export interface GeoPlanArticle {
  no: number;
  /** 周序号(2026-07-29 内容计划改「4 周 × 4 篇」)。month 键沿用旧名同值,消费方零改动。 */
  week?: number;
  month: number;
  title: string;
  channel: string;
  content_type: string;
  audience: string;
  target_question: string;
  purpose: string;
  affects_platforms: string[];
  affects_note: string;
  kpi: string;
}

/** 一周(旧口径为一个月)的排期块。month/week 同值,month 为向后兼容的旧键。 */
export interface GeoPlanPeriod {
  month: number;
  week?: number;
  theme: string;
  focus: string;
  articles: GeoPlanArticle[];
}

export interface GeoContentPlan {
  available?: boolean;
  total?: number;
  /** 旧键,与 weeks 同一份数据(后端两个键都发,老报告 JSON 只有 months)。 */
  months?: GeoPlanPeriod[];
  weeks?: GeoPlanPeriod[];
  /** 排期口径,如 {weeks:4, per_week:4, label:"4 周 × 每周 4 篇"}。老报告 JSON 无此键。 */
  cadence?: { weeks: number; per_week: number; label: string };
  wording_note?: string;
}

export interface GeoSourceInvestRow {
  source: string;
  current_citations: number;
  suggested_weight: string;
  reason: string;
  theme: string;
  affects_intents: string[];
}

export interface GeoSourceInvestment {
  available?: boolean;
  rows?: GeoSourceInvestRow[];
  wording_note?: string;
}

export interface GeoBaselineCompare {
  available?: boolean;
  baseline_run_id?: string;
  baseline_brand?: string;
  baseline_grade?: string;
  baseline_at?: string;
  same_caliber?: boolean;
  rows?: {
    key: string;
    label: string;
    baseline: number | null;
    current: number;
    delta: number | null;
  }[];
  note?: string;
}

export interface GeoShortboard {
  no: string;
  name: string;
  why: string;
  action: string;
}

/** 解析后的 GeoReport(ai 侧 snake_case;A4 起全量透出,新增字段全部可选保证旧报告兼容)。 */
export interface GeoReport {
  brand: string;
  industry: string;
  benchmark?: string; // E(反馈#8)手填行业标杆回显(未填为空)
  overall_score: number;
  grade: string;
  dimensions: {
    visibility: number;
    rank: number;
    breadth: number;
    sentiment: number;
    citation: number;
  };
  platform_scores: { platform: string; mention_rate: number; positive_rate?: number }[];
  competitor_ranking: [string, number][];
  probes?: GeoProbe[];
  citation_sources?: GeoCitationSource[];
  brand_portrait: { labels?: string[]; wanted?: string[]; gap?: string[] };
  negative_exposure: { rate?: number; samples?: string[] };
  recommendations: { titles?: string[]; sources?: unknown[]; topics?: GeoTopic[] };
  platform_presence?: GeoPresence;
  search_insight?: GeoSearchInsight;
  methodology?: string;
  estimated_fields?: string[];
  // ── S1 换源批新段(有 score_breakdown 即走 12 板块新版式;旧报告走 legacy 面板)──
  score_breakdown?: GeoScoreBreakdown;
  audience_stats?: GeoAudienceStats;
  trust_assets?: GeoTrustAssets;
  search_overview?: GeoSearchOverview;
  platform_details?: GeoPlatformDetails;
  content_plan?: GeoContentPlan;
  source_investment?: GeoSourceInvestment;
  baseline_compare?: GeoBaselineCompare;
  shortboards?: GeoShortboard[];
  /** 问题矩阵版本(问题集哈希;与基线同版本 = 同口径可对比)。 */
  question_matrix_version?: string;
}

/** 引擎内部名 → 中文展示名(与 ai engines.py 对齐;未知名原样显示)。 */
export const ENGINE_LABELS: Record<string, string> = {
  deepseek: "DeepSeek",
  qwen: "通义千问",
  glm: "智谱GLM",
  kimi: "Kimi",
  doubao: "豆包",
  minimax: "MiniMax 海螺",
  wenxin: "文心 ERNIE",
  hunyuan: "腾讯混元",
  spark: "讯飞星火",
  gpt: "ChatGPT",
};

export const engineLabel = (name: string): string => ENGINE_LABELS[name] ?? name;

/** 发起监测入参(E,反馈#8):品牌 + 手填增强(行业/竞品/标杆,均可空)。 */
export interface GeoRunInputs {
  brandHint?: string;
  industry?: string;
  competitors?: string[];
  benchmark?: string;
  // S2-T6 企业画像扩展(全可选,空=现状;消费面=问题矩阵 prompt + 受众表回显)
  coreProducts?: string;
  targetAudience?: string;
  expansionDirection?: string;
}

export const geoService = {
  /** 发起一轮监测(异步);返回 RUNNING 回执(含 id)。手填增强空则 ai 侧走产品树派生/自选兜底。 */
  createRun: (input: GeoRunInputs = {}) =>
    api.post<GeoRunSummary>(`${BASE}/runs`, {
      brandHint: input.brandHint ?? "",
      industry: input.industry ?? "",
      competitors: input.competitors ?? [],
      benchmark: input.benchmark ?? "",
      coreProducts: input.coreProducts ?? "",
      targetAudience: input.targetAudience ?? "",
      expansionDirection: input.expansionDirection ?? "",
    }),
  /** 最近监测轮次列表。 */
  listRuns: (limit = 20) => api.get<GeoRunSummary[]>(`${BASE}/runs?limit=${limit}`),
  /** 轮次详情(轮询用)。 */
  getRun: (id: number) => api.get<GeoRunDetail>(`${BASE}/runs/${id}`),
  /** 手动改指基线(S1;仅 SUCCESS 轮;后端事务内清旧立新,(租户,品牌) 内基线唯一)。 */
  repointBaseline: (id: number) => api.post<GeoRunSummary>(`${BASE}/runs/${id}/baseline`, {}),
  /**
   * 删除一轮监测(2026-07-29)。软删。后端拒:基线轮(要先改指基线)、RUNNING 轮(在途已预扣积分)。
   * 两种拒都返 CONFLICT + 人话 message,前端直接把 message 弹出来即可。
   */
  deleteRun: (id: number) => api.delete<GeoRunSummary>(`${BASE}/runs/${id}`),
};

/** 解析 report_json 字符串;失败返 null。 */
export function parseReport(json: string | null): GeoReport | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as GeoReport;
  } catch {
    return null;
  }
}
